import React, { FC, useState } from 'react';
import styles from './styles.module.css';
import * as translations from './strings';
import { getLangKey } from '../../../../models/Language';
import GreenTick from '../../../../views/integration_settings/images/ic-green-tick.svg';
import { Button, Dropdown, message } from 'antd';
import { AlertCircle, ExternalLink, Trash, X as Close } from 'lucide-react';
import cx from 'classnames';
import { deleteItem } from 'appJS/utils/fetch';
import { ErrorDetails, IntegrationCardError } from '../Error';

type Props = {
  taskId: number;
  taskLocalId: number;
  projectId?: number;
  isSmallView: boolean;
  connectedText?: string;
  buttonText?: string;
  href: string;
  error?: IntegrationCardError;
  taskConnectionId?: number;
  onRemoved: () => void;
};

const strings = translations[getLangKey()];

const Connected: FC<Props> = ({
  taskId,
  taskLocalId,
  projectId,
  isSmallView,
  connectedText,
  buttonText,
  href,
  error,
  taskConnectionId,
  onRemoved
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [removing, setRemoving] = useState<boolean>(false);

  const handleRemove = async () => {
    setRemoving(true);

    try {
      await deleteItem(
        `/projects/${projectId}/tasks/${taskId}/task_connections/${taskConnectionId}`
      );

      setIsMenuOpen(false);
      onRemoved();
    } catch (error) {
      message.error(strings.removeError);
      console.error(error);
    } finally {
      setRemoving(false);
    }
  };

  const items = [
    {
      label: <span>{strings.confirmRemove}</span>,
      icon: <Trash className={styles.trashIcon} />,
      key: '0',
      disabled: removing,
      onClick: handleRemove
    }
  ];

  const removeLink = (hasError = false) => {
    const handleVisibleChange = (visible: boolean) => {
      setIsMenuOpen(visible);
    };

    return (
      <div id="dropdown-container">
        <Dropdown
          trigger={['click']}
          menu={{ items }}
          onOpenChange={handleVisibleChange}
          open={isMenuOpen}
          getPopupContainer={() =>
            document.getElementById('dropdown-container')
          }
        >
          <Button
            type="link"
            size="small"
            className={cx(styles.removeButton, {
              [styles.errorAction]: hasError
            })}
            loading={removing}
          >
            {hasError ? strings.unlink : strings.removeLink}
            {!hasError && !removing && <Close className={styles.close} />}
          </Button>
        </Dropdown>
      </div>
    );
  };

  if (error) {
    return (
      <div className={styles.cardContent}>
        <div className={cx(styles.connectedContainer, styles.errorMessage)}>
          <AlertCircle className={styles.warningIcon} />
          <a
            title={href}
            className={styles.connectedButton}
            href={href}
            target="_blank"
            rel="noreferrer"
          >
            {`BH-${taskLocalId}`}
          </a>
          <span className={styles.returnedError}>{strings.returnedError}</span>
        </div>
        <div className={styles.errorActions}>
          <ErrorDetails error={error} />
          {!!taskConnectionId && (
            <>
              <span aria-hidden="true" className={styles.actionDivider}>
                |
              </span>
              {removeLink(true)}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.cardContent}>
      <div
        className={cx(styles.connectedContainer, {
          [styles.smallConnected]: !!isSmallView
        })}
      >
        <GreenTick className={styles.greenTickIcon} />
        {!href ? (
          <span className={styles.connectedText}>{strings.taskSent}</span>
        ) : (
          <>
            <span className={styles.connectedText}>{connectedText}</span>
            <a
              title={href}
              className={styles.connectedButton}
              href={href}
              target="_blank"
              rel="noreferrer"
            >
              {buttonText}
              {!isSmallView && <ExternalLink className={styles.linkIcon} />}
            </a>
          </>
        )}
      </div>
      {!!taskConnectionId && removeLink()}
    </div>
  );
};

export default Connected;
